import { createSelector } from 'reselect';

// these are the root fields in redux state
const selectLoginDomain = state => state.login;
const selectModalDomain = state => state.modal;
const selectRegistrationDomain = state => state.registration;
const selectNotificationDomain = state => state.notification;
const selectNavigationDomain = state => state.navigation;
const selectUserProfileDomain = state => state.userProfile;
const selectSkillsetDomain = state => state.skillset;
const selectJobsDomain = state => state.jobs;
const selectSettingsDomain = state => state.settings;
const selectBuilderApprovalDomain = state => state.builderApproval;
const selectBuilderPics = state => state.builderPics;

// note: all these selectors are memoized to improve performance

// login domain selectors
export const selectLoginLoadingStatus = () =>
  createSelector(
    selectLoginDomain,
    domain => domain.get('isLoading'),
  );

export const selectLoginError = () =>
  createSelector(
    selectLoginDomain,
    domain => domain.get('error'),
  );

export const selectLoginScreen = () =>
  createSelector(
    selectLoginDomain,
    domain => domain.get('loginScreen'),
  );

export const selectIsAuthenticated = () =>
  createSelector(
    selectLoginDomain,
    domain => domain.get('isAuthenticated'),
  );

export const selectMessage = () =>
  createSelector(
    selectLoginDomain,
    domain => domain.get('message'),
  );

export const selectVerificationCode = () =>
  createSelector(
    selectLoginDomain,
    domain => domain.get('verificationCode'),
  );

export const selectVerificationEmail = () =>
  createSelector(
    selectLoginDomain,
    domain => domain.get('verificationEmail'),
  );

// modal domain selectors
export const selectModalName = () =>
  createSelector(
    selectModalDomain,
    domain => domain.get('name'),
  );

export const selectModalIsOpen = () =>
  createSelector(
    selectModalDomain,
    domain => domain.get('isOpen'),
  );

export const selectModalProps = () =>
  createSelector(
    selectModalDomain,
    domain => domain.get('props').toJS(),
  );

// registration domain selectors
export const selectRegistrationInfo = () =>
  createSelector(
    selectRegistrationDomain,
    domain => domain.get('info').toJS(),
  );

export const selectRegistrationCurrentStep = () =>
  createSelector(
    selectRegistrationDomain,
    domain => domain.get('currentStep'),
  );

export const selectRegistrationIsSubmitting = () =>
  createSelector(
    selectRegistrationDomain,
    domain => domain.get('isSubmitting'),
  );

// notifications domain selectors
export const selectNotifications = () =>
  createSelector(
    selectNotificationDomain,
    domain => domain.get('notifications').toJS(),
  );

// userProfile domain selectors
export const selectUserAttributes = () =>
  createSelector(
    selectUserProfileDomain,
    domain => domain.get('userAttributes').toJS(),
  );

export const selectBusinessAttributes = () =>
  createSelector(
    selectUserProfileDomain,
    domain => domain.get('businessAttributes').toJS(),
  );

export const selectIsUpdatingPersonalProfile = () =>
  createSelector(
    selectUserProfileDomain,
    domain => domain.get('isUpdatingPersonalProfile'),
  );

export const selectIsUpdatingBusinessProfile = () =>
  createSelector(
    selectUserProfileDomain,
    domain => domain.get('isUpdatingBusinessProfile'),
  );

export const selectIsUpdatingServiceArea = () =>
  createSelector(
    selectUserProfileDomain,
    domain => domain.get('isUpdatingServiceArea'),
  );

export const selectIsUpdatingAdditionalInfo = () =>
  createSelector(
    selectUserProfileDomain,
    domain => domain.get('isUpdatingAdditionalInfo'),
  );

export const selectPersonalProfileFormMode = () =>
  createSelector(
    selectUserProfileDomain,
    domain => domain.get('personalProfileFormMode'),
  );

export const selectBusinessProfileFormMode = () =>
  createSelector(
    selectUserProfileDomain,
    domain => domain.get('businessProfileFormMode'),
  );

export const selectServiceAreaFormMode = () =>
  createSelector(
    selectUserProfileDomain,
    domain => domain.get('serviceAreaFormMode'),
  );

export const selectAdditionalInfoFormMode = () =>
  createSelector(
    selectUserProfileDomain,
    domain => domain.get('additionalInfoFormMode'),
  );

export const selectIsGettingUserProfile = () =>
  createSelector(
    selectUserProfileDomain,
    domain => domain.get('isGettingUserProfile'),
  );

// skillset domain selectors
export const selectSkillsetBuildOptions = () =>
  createSelector(
    selectSkillsetDomain,
    domain => domain.get('build').toJS(),
  );

export const selectSkillsetLookOptions = () =>
  createSelector(
    selectSkillsetDomain,
    domain => domain.get('look').toJS(),
  );

export const selectJobs = () =>
  createSelector(
    selectJobsDomain,
    domain => domain.get('jobs').toJS(),
  );

export const selectJobsLoading = () =>
  createSelector(
    selectJobsDomain,
    domain => domain.get('isLoading'),
  );

export const selectJobsRetrieved = () =>
  createSelector(
    selectJobsDomain,
    domain => domain.get('jobsRetrieved'),
  );

export const selectIsJobLoading = () =>
  createSelector(
    selectJobsDomain,
    domain => domain.getIn(['job', 'isLoading']),
  );

export const selectJobsCount = () =>
  createSelector(
    selectJobsDomain,
    domain => domain.get('jobsCount').toJS(),
  );

export const selectIsCreatingJob = () =>
  createSelector(
    selectJobsDomain,
    domain => domain.getIn(['job', 'isCreatingJob']),
  );

// navigation domain selectors
export const selectNavigationLoadingStatus = () =>
  createSelector(
    selectNavigationDomain,
    domain => domain.get('isLoading'),
  );

export const selectNavigationError = () =>
  createSelector(
    selectNavigationDomain,
    domain => domain.get('error'),
  );

// settings domain selectors
export const selectEmailNotificationSettingsUpdatingStatus = () =>
  createSelector(
    selectSettingsDomain,
    domain => domain.getIn(['notification', 'email', 'isUpdating']), // todo: figure out why NOTIFICATION_TYPES.EMAIL can not be used in place of 'email'
  );

export const selectSMSNotificationSettingsUpdatingStatus = () =>
  createSelector(
    selectSettingsDomain,
    domain => domain.getIn(['notification', 'SMS', 'isUpdating']), // todo: figure out why NOTIFICATION_TYPES.SMS can not be used in place of 'SMS'
  );

export const selectIsApproveBuilderLoading = () =>
  createSelector(
    selectBuilderApprovalDomain,
    domain => domain.get('isLoading'),
  );

export const selectIsBuilderApproved = () =>
  createSelector(
    selectBuilderApprovalDomain,
    domain => domain.get('isApproved'),
  );

// builder Pics
export const selectBuilderProjectGallery = () =>
  createSelector(
    selectBuilderPics,
    domain => domain.get('builderProjectGallery').toJS(),
  );
